/**
 * The canvas cursors, one per tool and mode, as SVG — the source both for the build script
 * that bakes them into PNGs (`scripts/tool-cursors.mjs`) and for the tool guides, which
 * show the same cursor doing the tool's work.
 *
 * The cursor IS the tool, never an arrow with a badge: a pen nib, a pencil, scissors, an
 * I-beam — each with its hotspot on the part that acts. Tools that place something by a
 * point (shapes, artboards, slices, measures) wear a crosshair whose centre is the point,
 * with what it makes beside it; tools that work on what is chosen keep the arrow.
 *
 * Every mark is drawn twice, white and wide underneath and ink on top, so it reads on
 * black artwork and on white paper alike. 32 × 32, one native size (see toolFeedback.ts
 * for why there is no @2x in the CSS).
 */

const INK = '#1d1b22';
const HALO = '#ffffff';

/** A mark with its halo: `inner` drawn once wide in white, then in ink. */
function haloed(inner, { width = 1.5, halo = 3.2, fill = true } = {}) {
  const under = inner.replaceAll('%F', fill ? HALO : 'none').replaceAll('%S', HALO).replaceAll('%W', String(width + halo));
  const over = inner.replaceAll('%F', fill ? INK : 'none').replaceAll('%S', INK).replaceAll('%W', String(width));
  return `<g stroke-linecap="round" stroke-linejoin="round">${under}${over}</g>`;
}

/** A shape in ink with a white edge — for filled instruments. */
function solid(d, extra = '') {
  return `<path d="${d}" fill="${INK}" stroke="${HALO}" stroke-width="1.6" stroke-linejoin="round" paint-order="stroke" ${extra}/>`;
}

/** Local drawing whose tip is at (0, 0) and whose body runs up, laid at 45° with the tip at (x, y). */
function instrument(body, x = 4, y = 28) {
  return `<g transform="translate(${x} ${y}) rotate(45)">${body}</g>`;
}

// ── the arrow ────────────────────────────────────────────────────────────
const ARROW_D = 'M22.66 9.29 2.1 0.79A1 1 0 0 0 0.8 2.1l8.49 20.56a1 1 0 0 0 1.88 -0.11l2.49 -8.72a0.28 0.28 0 0 1 0.18 -0.17l8.71 -2.49a1 1 0 0 0 0.11 -1.88Z';
const arrow = (hollow = false) =>
  `<path transform="translate(1.5 1.5) scale(.72)" d="${ARROW_D}" fill="${hollow ? HALO : INK}" stroke="${hollow ? INK : HALO}" stroke-width="${hollow ? 1.9 : 2.1}" stroke-linejoin="round"/>`;

// ── the crosshair ────────────────────────────────────────────────────────
const CROSS = 10;
const crosshair = () =>
  haloed(`<path d="M${CROSS - 8} ${CROSS}h5.5M${CROSS + 2.5} ${CROSS}h5.5M${CROSS} ${CROSS - 8}v5.5M${CROSS} ${CROSS + 2.5}v5.5" stroke="%S" stroke-width="%W" fill="none"/>`, { width: 1.3, halo: 3 });

/** A small outlined glyph in the corner (12 × 12 box at 18, 18). */
const glyph = (inner, opts) => `<g transform="translate(18 18)">${haloed(inner, { width: 1.4, halo: 2.4, ...opts })}</g>`;
const line = (d) => `<path d="${d}" stroke="%S" stroke-width="%W" fill="none"/>`;
const filled = (d) => `<path d="${d}" stroke="%S" stroke-width="%W" fill="%F"/>`;

const G = {
  rect: line('M1.5 2.5h9v7h-9Z'),
  ellipse: line('M6 2.2c2.6 0 4.6 1.7 4.6 3.8S8.6 9.8 6 9.8 1.4 8.1 1.4 6 3.4 2.2 6 2.2Z'),
  polygon: line('M3.5 1.8h5l2.6 4.2-2.6 4.2h-5L0.9 6Z'),
  star: line('M6 1l1.5 3.2 3.4.4-2.5 2.3.7 3.4L6 8.6 2.9 10.3l.7-3.4L1.1 4.6l3.4-.4Z'),
  line: line('M2 10 10 2') + filled('M1 10a1 1 0 1 0 2 0a1 1 0 1 0-2 0M9 2a1 1 0 1 0 2 0a1 1 0 1 0-2 0'),
  arc: line('M1.5 10.5A9 9 0 0 1 10.5 1.5'),
  artboard: line('M1.5 3.5h9v7h-9ZM1.5 1h4'),
  slice: line('M1.5 1.5h9v9h-9ZM1.5 10.5l9-9'),
  measure: line('M1 6h10M1 3v6M11 3v6'),
  angle: line('M1.5 10.5h9.5M1.5 10.5l6.5-8M5.5 10.5a4 4 0 0 0-1.5-3.1'),
  radius: line('M6 1.5a4.5 4.5 0 1 0 0 9a4.5 4.5 0 1 0 0-9ZM6 6h4.5'),
  textArea: line('M1 1.5h10v9H1ZM3.8 4h4.4M6 4v4.5'),
  join: filled('M1.5 9.5a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0-2.6 0M7.9 2.5a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0-2.6 0') + line('M4.1 8.6 7.9 3.4'),
  builder: line('M4.5 2.5a3.5 3.5 0 1 0 0 7a3.5 3.5 0 1 0 0-7ZM7.5 2.5a3.5 3.5 0 1 0 0 7a3.5 3.5 0 1 0 0-7Z'),
  blend: filled('M0.8 6a2.2 2.2 0 1 0 4.4 0a2.2 2.2 0 1 0-4.4 0') + line('M7.5 3.8h3.7v4.4H7.5Z'),
  cloner: filled('M1 1h4v4H1ZM7 1h4v4H7ZM1 7h4v4H1ZM7 7h4v4H7Z'),
  width: filled('M0.8 6Q6 0.6 11.2 6Q6 11.4 0.8 6Z'),
  smooth: line('M1 7.5c2-4 3.5-4 5 0s3 4 5 0'),
  warp: line('M1 4c2.5-2 4-2 5 0s2.5 2 5 0M1 8.5c2.5-2 4-2 5 0s2.5 2 5 0'),
  twirl: line('M6 6.2c0-.9 1.4-.9 1.4.2 0 1.6-2.8 1.8-3.1-.2-.3-2.5 3.3-3.6 4.7-1.3 1.6 2.6-.6 6-4 5.6-3.1-.4-4.4-3.9-2.8-6.6'),
  pucker: line('M1.5 1.5Q6 5 10.5 1.5Q7 6 10.5 10.5Q6 7 1.5 10.5Q5 6 1.5 1.5Z'),
  bloat: line('M6 1.3c2.7 0 4.8 2 4.8 4.7S8.7 10.7 6 10.7 1.2 8.7 1.2 6 3.3 1.3 6 1.3Z') + line('M3.8 6h4.4M6 3.8v4.4'),
  scallop: line('M3 3a1.6 1.6 0 0 1 3-.8 1.6 1.6 0 0 1 3 .8 1.6 1.6 0 0 1 .8 3 1.6 1.6 0 0 1-.8 3 1.6 1.6 0 0 1-3 .8 1.6 1.6 0 0 1-3-.8 1.6 1.6 0 0 1-.8-3A1.6 1.6 0 0 1 3 3Z'),
  crystallize: line('M6 .8 7.2 4.2 10.8 3 8.6 6l2.2 3-3.6-1.2L6 11.2 4.8 7.8 1.2 9 3.4 6 1.2 3l3.6 1.2Z'),
  wrinkle: line('M1 6l1.6-2.4L4.2 8l1.6-4.4L7.4 8 9 3.6 11 6'),
  scale: line('M1.5 4.5h6v6h-6ZM6.5 1.5h4v4M10.5 1.5 7.5 4.5'),
  perspective: line('M3 2.5h6l2 7.5H1Z'),
  distort: line('M2.5 1.5l8 1.5-1.5 7.5-7-1.5Z'),
  addon: line('M1.5 3.5h2.8a1.6 1.6 0 1 1 3.2 0h2.8v2.3a1.6 1.6 0 1 1 0 3.2v2.3h-8.8Z'),
  dashedBox: `<path d="M1.5 1.5h9v9h-9Z" stroke="%S" stroke-width="%W" fill="none" stroke-dasharray="2.2 1.6"/>`,
  loop: line('M6 1.5c3 0 4.8 1.4 4.8 3.3S8.4 8 5.6 8C3 8 1.2 6.8 1.2 5 1.2 3 3 1.5 6 1.5ZM4 8c-.6 1.1-.2 2.2.8 2.6'),
  plus: line('M3 6h6M6 3v6'),
  minus: line('M3 6h6'),
  corner: line('M2.5 9 6 3l3.5 6'),
  sparkle: line('M9 1v3.2M7.4 2.6h3.2M3 6.5v2M2 7.5h2'),
};

// ── the instruments (tip at 0,0, body up) ───────────────────────────────
const PEN = solid('M0 0 -5.4 -9.2 -3.2 -16.2h6.4L5.4 -9.2Z') +
  `<path d="M0 -1.6V-7.6" stroke="${HALO}" stroke-width="1.2" stroke-linecap="round"/><circle cx="0" cy="-9.4" r="1.45" fill="${HALO}"/>` +
  solid('M-3.8 -20.6h7.6v3.2h-7.6Z');
const PENCIL = solid('M0 0 -3.3 -6.2V-21.5h6.6V-6.2Z') +
  `<path d="M-3.3 -6.2h6.6" stroke="${HALO}" stroke-width="1.1"/><path d="M-1 -1.9 0 0 1 -1.9Z" fill="${HALO}"/>` +
  solid('M-3.3 -25.6h6.6v3.1h-6.6Z');
const BRUSH = solid('M0 0C-2.9 -2.6-3.8 -7-3.1 -10.2h6.2C3.8 -7 2.9 -2.6 0 0Z') +
  solid('M-3.3 -13.5h6.6v3.3h-6.6Z') + solid('M-2.3 -13.5-1.4 -26h2.8l.9 12.5Z');
const ERASER = `<path d="M-4.6 -6.4h9.2V-1a1.2 1.2 0 0 1-1.2 1.2h-6.8A1.2 1.2 0 0 1-4.6 -1Z" fill="${HALO}" stroke="${INK}" stroke-width="1.4" stroke-linejoin="round"/>` +
  solid('M-4.6 -6.4h9.2V-18a1.6 1.6 0 0 0-1.6-1.6h-6a1.6 1.6 0 0 0-1.6 1.6Z');
const KNIFE = solid('M0 0C-4.6 -4.4-5.6 -9.8-5.2 -15.2H1.6V-2.2Z') +
  `<path d="M-3.9 -12.6h4.2" stroke="${HALO}" stroke-width="1" stroke-linecap="round" opacity=".7"/>` +
  solid('M-3.4 -27a1.6 1.6 0 0 1 1.6-1.6h3a1.6 1.6 0 0 1 1.6 1.6v9.4h-6.2Z');
const PIN = `<path d="M0 0V-8" stroke="${HALO}" stroke-width="3.4" stroke-linecap="round"/><path d="M0 0V-8" stroke="${INK}" stroke-width="1.4" stroke-linecap="round"/>` +
  solid('M-4.6 -8.4h9.2l-1.6 -3h-6Z') + solid('M-3 -11.4-2.2 -17.4h4.4l.8 6Z') + solid('M-4.4 -17.4h8.8v-2.6h-8.8Z');

// Scissors lie on their side, cutting to the left; the tips meet at (4, 16).
const SCISSORS =
  haloed(line('M15.5 14.8 19.4 11.6M15.5 17.2 19.4 20.4') + line('M22.2 7.6a3.2 3.2 0 1 0 .01 0ZM22.2 18.1a3.2 3.2 0 1 0 .01 0Z'), { width: 2, halo: 3 }) +
  solid('M16.6 15.2 4 13.8 3.4 16Z') + solid('M16.6 16.8 4 18.2 3.4 16Z') +
  `<circle cx="15.2" cy="16" r="1.1" fill="${HALO}"/>`;

// The I-beam, its middle the hotspot.
const IBEAM = haloed(line('M16 9.5v13M12.8 8.4c1.8 0 3.2.4 3.2 2 0-1.6 1.4-2 3.2-2M12.8 23.6c1.8 0 3.2-.4 3.2-2 0 1.6 1.4 2 3.2 2'), { width: 1.5, halo: 3.2 });

const svg = (inner) => `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">${inner}</svg>\n`;
const cross = (mark) => ({ hotspot: [CROSS, CROSS], svg: svg(crosshair() + glyph(mark)) });
const pointer = (mark, hollow = false) => ({ hotspot: [2, 2], svg: svg(arrow(hollow) + (mark ? glyph(mark) : '')) });
const tool = (body, mark) => ({ hotspot: [4, 28], svg: svg(instrument(body) + (mark ? `<g transform="translate(18 18)">${haloed(mark, { width: 1.5, halo: 3.2 })}</g>` : '')) });

/**
 * Every cursor, by the tool guide's key: the tool id, or `tool:mode` for a mode that
 * works differently. A key missing here falls back to its tool's, then to the arrow.
 */
export const CURSOR_ART = {
  select: pointer(null),
  'select:point': pointer(null, true),
  'select:area': pointer(G.dashedBox, true),
  'select:lasso': pointer(G.loop, true),

  rect: cross(G.rect),
  ellipse: cross(G.ellipse),
  polygon: cross(G.polygon),
  star: cross(G.star),
  line: cross(G.line),
  arc: cross(G.arc),
  artboard: cross(G.artboard),
  slice: cross(G.slice),
  'slice:choose': pointer(G.slice),
  measure: cross(G.measure),
  'measure:angular': cross(G.angle),
  'measure:radial': cross(G.radius),

  text: { hotspot: [16, 16], svg: svg(IBEAM) },
  'text:area': cross(G.textArea),
  'text:path': { hotspot: [16, 16], svg: svg(IBEAM + `<g transform="translate(19 21)">${haloed(G.smooth, { width: 1.4, halo: 3.2 })}</g>`) },

  pen: tool(PEN),
  'pen:add': tool(PEN, G.plus),
  'pen:remove': tool(PEN, G.minus),
  'pen:convert': tool(PEN, G.corner),

  draw: tool(PENCIL),
  'draw:pencil': tool(PENCIL),
  'draw:shape': tool(PENCIL, G.sparkle),
  'draw:blob': tool(BRUSH),
  'draw:smooth': cross(G.smooth),
  'draw:erase': tool(ERASER),
  'draw:join': cross(G.join),

  liquify: cross(G.warp),
  'liquify:warp': cross(G.warp),
  'liquify:twirl': cross(G.twirl),
  'liquify:pucker': cross(G.pucker),
  'liquify:bloat': cross(G.bloat),
  'liquify:scallop': cross(G.scallop),
  'liquify:crystallize': cross(G.crystallize),
  'liquify:wrinkle': cross(G.wrinkle),
  width: cross(G.width),

  cut: tool(ERASER),
  'cut:erase': tool(ERASER),
  'cut:scissors': { hotspot: [4, 16], svg: svg(SCISSORS) },
  'cut:knife': tool(KNIFE),

  warp: pointer(G.scale),
  'warp:transform': pointer(G.scale),
  'warp:perspective': pointer(G.perspective),
  'warp:distort': pointer(G.distort),
  'warp:puppet': tool(PIN),

  builder: cross(G.builder),
  blend: cross(G.blend),
  cloner: pointer(G.cloner),
  addon: pointer(G.addon),
};
